import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'
import { Decimal } from '~/composables/support/decimal'

export const AnalyticsCreateCheckoutResultFragment = graphql(`
    fragment AnalyticsCreateCheckoutResultFragment on CreateCheckoutResult {
        record {
            id
            subtotal
            inclusiveTaxTotal
            exclusiveTaxTotal
            facility {
                id
                organization {
                    googleAnalyticsTag
                    googleAnalyticsTagEcommerceEvents
                    dataLayerEcommerceEvents
                    currencyFormat
                }
            }
            items {
                __typename
                ... on CartContractItem {
                    lineItems {
                        quantity
                        unitPrice
                        originalUnitPrice
                        promotionCode
                        productVariant {
                            id
                            variantDisplay
                            product {
                                description
                            }
                        }
                        enrollment {
                            enrollmentType {
                                singular
                            }
                        }
                    }
                    contract {
                        __typename
                        ... on PlanContract {
                            planType
                            publicName
                        }
                    }
                }
            }
        }
    }
`)

export function emitCreateCheckoutEvent(
    createCheckoutResult: FragmentType<
        typeof AnalyticsCreateCheckoutResultFragment
    >
) {
    const createCheckoutResultData = getFragmentData(
        AnalyticsCreateCheckoutResultFragment,
        createCheckoutResult
    )

    if (createCheckoutResultData.record === null) {
        // Payment only - no invoice created
        return
    }

    const locationId = createCheckoutResultData.record.facility.id

    const items = createCheckoutResultData.record.items.flatMap(
        (item, itemIndex) => {
            if (item.__typename !== 'CartContractItem') {
                return
            }

            let contractInfo:
                | { planType: string; planName: string }
                | undefined = undefined
            if (
                item.contract !== null &&
                item.contract.__typename === 'PlanContract'
            ) {
                contractInfo = {
                    planType: item.contract.planType,
                    planName: item.contract.publicName
                }
            }

            return item.lineItems.map((lineItem, lineItemIndex) => {
                return {
                    item_id: lineItem.productVariant.id,
                    item_name: lineItem.productVariant.product.description,
                    coupon: lineItem.promotionCode,
                    discount: Number(
                        new Decimal(lineItem.unitPrice)
                            .minus(new Decimal(lineItem.originalUnitPrice))
                            .toFixed(2)
                    ),
                    item_variant: lineItem.productVariant.variantDisplay,
                    location_id: locationId,
                    price: Number(lineItem.unitPrice),
                    quantity: lineItem.quantity,

                    //  Custom fields
                    index: itemIndex + lineItemIndex,
                    plan_type: contractInfo?.planType,
                    plan_name: contractInfo?.planName,
                    enrollment_type:
                        lineItem.enrollment?.enrollmentType.singular
                }
            })
        }
    )

    const eventData = {
        // Sum of (price * quantity) for all items.
        value: Number(createCheckoutResultData.record.subtotal),
        currency:
            createCheckoutResultData.record.facility.organization
                .currencyFormat,
        items: [...items]
    }

    if (
        createCheckoutResultData.record.facility.organization
            .googleAnalyticsTag !== '' &&
        createCheckoutResultData.record.facility.organization
            .googleAnalyticsTagEcommerceEvents
    ) {
        try {
            // Begin_checkout. A customer started the checkout process
            // @ts-ignore : ignore gtag not defined
            gtag('event', 'begin_checkout', eventData)
        } catch {}
    }

    if (
        createCheckoutResultData.record.facility.organization
            .dataLayerEcommerceEvents
    ) {
        try {
            // Begin_checkout. A customer started the checkout process
            // @ts-ignore : Ignore dataLayer object not defined
            window.dataLayer.push({
                event: 'begin_checkout',
                ...eventData
            })
        } catch {}
    }
}
